import { useEffect, useState } from 'react'
import * as RadixTabs from '@radix-ui/react-tabs'
import cx from 'classnames'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import Modal from 'common/components/Modal'
import { Clipboard, Email, File as FileIcon, ImageIcon } from 'common/icons'

import { extractEmlContent } from './helpers/extractEmlContent'
import { extractPdfText } from './helpers/extractPdfText'
import { renderPdfThumbnail } from './helpers/renderPdfThumbnail'
import EmlImportTabContent, { EmlSelection } from './EmlImportTabContent'
import ImageImportTabContent, {
  buildImageSelection,
  ImageSelection,
} from './ImageImportTabContent'
import PasteHtmlTabContent from './PasteHtmlTabContent'
import PdfImportTabContent, { PdfSelection } from './PdfImportTabContent'

export type ImportTab = 'html' | 'pdf' | 'image' | 'eml'

type Props = {
  isOpen: boolean
  initialTab: ImportTab
  currentDescription?: string
  onClose: () => void
  onImportHtml: (html: string) => void
  onImportPdf: (text: string) => void
  onImportImage: (dataUri: string) => void
  onImportEml: (html: string) => void
}

const tabTriggerClass = cx(
  'flex items-center pb-3 mr-6 text-sm font-medium uppercase tracking-wide',
  'text-coolGray-400 border-b-2 border-transparent outline-none',
  'hover:text-base-700 cursor-pointer',
  'data-[state=active]:text-base-700 data-[state=active]:border-base-700'
)

const extractTextSafely = async (file: File) => {
  try {
    const buffer = await file.arrayBuffer()
    return { success: true as const, text: await extractPdfText(buffer) }
  } catch {
    return { success: false as const, text: null }
  }
}

const renderThumbnailSafely = async (file: File) => {
  try {
    const buffer = await file.arrayBuffer()
    return await renderPdfThumbnail(buffer)
  } catch {
    // Thumbnail is best-effort — a render failure shouldn't block import as
    // long as text extraction succeeded.
    return null
  }
}

const ImportContentModal = ({
  isOpen,
  initialTab,
  currentDescription,
  onClose,
  onImportHtml,
  onImportPdf,
  onImportImage,
  onImportEml,
}: Props) => {
  const [activeTab, setActiveTab] = useState<ImportTab>(initialTab)
  const [html, setHtml] = useState('')
  const [pdfSelection, setPdfSelection] = useState<PdfSelection | null>(null)
  const [imageSelection, setImageSelection] = useState<ImageSelection | null>(
    null
  )
  const [emlSelection, setEmlSelection] = useState<EmlSelection | null>(null)
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false)

  // Reopen on whichever tab was clicked in the menu, each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab)
    }
  }, [isOpen, initialTab])

  const hasExistingContent = !!currentDescription?.trim()

  // The preview is a blob: object URL — unlike the PDF tab's data-URI
  // thumbnail, it must be revoked explicitly or it leaks for the page's
  // lifetime.
  const revokeImagePreview = () => {
    if (imageSelection?.previewUrl) {
      URL.revokeObjectURL(imageSelection.previewUrl)
    }
  }

  const handleClose = () => {
    setHtml('')
    setPdfSelection(null)
    revokeImagePreview()
    setImageSelection(null)
    setEmlSelection(null)
    onClose()
  }

  const handlePdfFileSelected = async (file: File) => {
    setPdfSelection({
      file,
      thumbnailUrl: null,
      extractedText: null,
      isProcessing: true,
      error: null,
    })

    const [textOutcome, thumbnailUrl] = await Promise.all([
      extractTextSafely(file),
      renderThumbnailSafely(file),
    ])

    if (!textOutcome.success || !textOutcome.text?.trim()) {
      setPdfSelection({
        file,
        thumbnailUrl,
        extractedText: null,
        isProcessing: false,
        error: textOutcome.success
          ? 'No readable text was found in this PDF. Try a different file.'
          : 'Could not read this PDF. Please try a different file.',
      })
      return
    }

    setPdfSelection({
      file,
      thumbnailUrl,
      extractedText: textOutcome.text,
      isProcessing: false,
      error: null,
    })
  }

  const handleImageFileSelected = async (file: File) => {
    revokeImagePreview()
    setImageSelection({
      file,
      previewUrl: null,
      dataUri: null,
      isProcessing: true,
      error: null,
    })

    setImageSelection(await buildImageSelection(file))
  }

  const handleEmlFileSelected = async (file: File) => {
    setEmlSelection({
      file,
      subject: null,
      html: null,
      isProcessing: true,
      error: null,
    })

    try {
      const buffer = await file.arrayBuffer()
      const { subject, html: emlHtml } = await extractEmlContent(buffer)

      setEmlSelection({
        file,
        subject,
        html: emlHtml,
        isProcessing: false,
        error: emlHtml
          ? null
          : 'No HTML content was found in this email. Try a different file.',
      })
    } catch {
      setEmlSelection({
        file,
        subject: null,
        html: null,
        isProcessing: false,
        error: 'Could not read this email. Please try a different file.',
      })
    }
  }

  // The actual import — only ever called once we know it's safe to replace
  // any existing brief content (either there was none, or the user just
  // confirmed the replace-content warning).
  const performImport = () => {
    if (activeTab === 'html') {
      onImportHtml(html)
      setHtml('')
    } else if (activeTab === 'pdf' && pdfSelection?.extractedText) {
      onImportPdf(pdfSelection.extractedText)
      setPdfSelection(null)
    } else if (activeTab === 'image' && imageSelection?.dataUri) {
      onImportImage(imageSelection.dataUri)
      setImageSelection(null)
    } else if (activeTab === 'eml' && emlSelection?.html) {
      onImportEml(emlSelection.html)
      setEmlSelection(null)
    }
  }

  const handleImportClick = () => {
    if (hasExistingContent) {
      setIsReplaceConfirmOpen(true)
      return
    }
    performImport()
  }

  const handleConfirmReplace = () => {
    setIsReplaceConfirmOpen(false)
    performImport()
  }

  const isPdfBusy = !!pdfSelection?.isProcessing
  const isImageBusy = !!imageSelection?.isProcessing
  const isEmlBusy = !!emlSelection?.isProcessing
  const isImportDisabled =
    activeTab === 'html'
      ? !html.trim()
      : activeTab === 'pdf'
        ? !pdfSelection?.extractedText
        : activeTab === 'image'
          ? !imageSelection?.dataUri
          : !emlSelection?.html

  const importButtonLabel =
    activeTab === 'pdf' && isPdfBusy
      ? 'Extracting...'
      : activeTab === 'image' && isImageBusy
        ? 'Uploading...'
        : activeTab === 'eml' && isEmlBusy
          ? 'Extracting...'
          : activeTab === 'html'
            ? 'Import HTML'
            : activeTab === 'pdf'
              ? 'Import PDF'
              : activeTab === 'image'
                ? 'Import Image'
                : 'Import Email'

  const contentTypeLabel =
    activeTab === 'pdf'
      ? 'PDF'
      : activeTab === 'image'
        ? 'image'
        : activeTab === 'eml'
          ? 'email'
          : 'HTML'

  return (
    <>
      <Modal
        visible={isOpen && !isReplaceConfirmOpen}
        onClose={handleClose}
        title="Import content"
        size="large"
        // Warm cream tint for this modal only (via inline style — Modal.tsx
        // skips its default bg-white when a custom background is passed,
        // since this project's Tailwind `important: true` config means a
        // plain inline style can't otherwise win against that !important class).
        style={{ backgroundColor: '#fbf7f0' }}
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                isImportDisabled || isPdfBusy || isImageBusy || isEmlBusy
              }
              onClick={handleImportClick}
              data-testid="import-content-button"
            >
              {importButtonLabel}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col h-full">
          <p className="text-base text-base-700 pb-4 shrink-0">
            Import or paste the source of an existing campaign to reuse its
            structure and copy as the starting point for your brief.
          </p>
          <RadixTabs.Root
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as ImportTab)}
            className="flex flex-col flex-1 min-h-0"
          >
            <RadixTabs.List className="flex border-b border-gold-400 mb-4 shrink-0">
              <RadixTabs.Trigger
                value="html"
                className={tabTriggerClass}
                data-testid="import-tab-html"
              >
                <Clipboard isDefaultColor={false} className="mr-2" size={4} />
                Paste HTML
              </RadixTabs.Trigger>
              <RadixTabs.Trigger
                value="pdf"
                className={tabTriggerClass}
                data-testid="import-tab-pdf"
              >
                <FileIcon isDefaultColor={false} className="mr-2" size={4} />
                Attach PDF
              </RadixTabs.Trigger>
              <RadixTabs.Trigger
                value="image"
                className={tabTriggerClass}
                data-testid="import-tab-image"
              >
                <ImageIcon isDefaultColor={false} className="mr-2" size={4} />
                Attach Image
              </RadixTabs.Trigger>
              <RadixTabs.Trigger
                value="eml"
                className={tabTriggerClass}
                data-testid="import-tab-eml"
              >
                <Email isDefaultColor={false} className="mr-2" size={4} />
                Attach Email
              </RadixTabs.Trigger>
            </RadixTabs.List>

            {/* Only the active tab's Content is rendered — Radix normally
                hides the inactive one via a `hidden` attribute, but a `flex`
                class on Content wins the cascade over that, so both panels
                would otherwise sit stacked in the same flex column at once. */}
            {activeTab === 'html' ? (
              <RadixTabs.Content
                value="html"
                className="flex flex-col flex-1 min-h-0"
              >
                <PasteHtmlTabContent html={html} onHtmlChange={setHtml} />
              </RadixTabs.Content>
            ) : activeTab === 'pdf' ? (
              <RadixTabs.Content
                value="pdf"
                className="flex flex-col flex-1 min-h-0"
              >
                <PdfImportTabContent
                  selection={pdfSelection}
                  onFileSelected={handlePdfFileSelected}
                  onRemoveFile={() => setPdfSelection(null)}
                />
              </RadixTabs.Content>
            ) : activeTab === 'image' ? (
              <RadixTabs.Content
                value="image"
                className="flex flex-col flex-1 min-h-0"
              >
                <ImageImportTabContent
                  selection={imageSelection}
                  onFileSelected={handleImageFileSelected}
                  onRemoveFile={() => {
                    revokeImagePreview()
                    setImageSelection(null)
                  }}
                />
              </RadixTabs.Content>
            ) : (
              <RadixTabs.Content
                value="eml"
                className="flex flex-col flex-1 min-h-0"
              >
                <EmlImportTabContent
                  selection={emlSelection}
                  onFileSelected={handleEmlFileSelected}
                  onRemoveFile={() => setEmlSelection(null)}
                />
              </RadixTabs.Content>
            )}
          </RadixTabs.Root>
        </div>
      </Modal>

      <ConfirmationModal
        open={isReplaceConfirmOpen}
        title="Replace brief description"
        confirmationText={`Importing this ${contentTypeLabel} will generate a new brief description from it, replacing what's there now.`}
        confirmButtonText="Proceed"
        cancelButtonText="Cancel"
        confirmButtonVariant="primary"
        onCancel={() => setIsReplaceConfirmOpen(false)}
        onConfirm={handleConfirmReplace}
        // Match the Import content modal it's stacked on top of, rather than
        // the default white.
        style={{ backgroundColor: '#fbf7f0' }}
      />
    </>
  )
}

export default ImportContentModal
